// components/shared/CourseCard/utils.ts

export type CourseType = 'placeholder' | 'suggestion' | 'other';

export interface CourseLikeForType {
  isSuggestion?: boolean;
  requirementKey?: string | null;
  genEdArea?: string | null;
}

export const getCourseType = (
  courseCode: string | undefined,
  course: CourseLikeForType | undefined | null
): CourseType => {
  if (course?.isSuggestion) {
    return 'suggestion';
  }

  if (courseCode?.startsWith('placeholder-')) {
    return 'placeholder';
  }

  return 'other';
};

export const getCourseTypeStyle = (courseType: CourseType): string => {
  // UI Phase 2.2: warm token language (surface/line tokens; suggestions use
  // the status-suggested token so the whole status scale stays one contract).
  switch (courseType) {
    case 'placeholder':
      // border-2 to match the suggestion tiles' dash weight (owner feedback:
      // the 1px dash read thinner than every other card in the column).
      return 'bg-surface-card border-2 border-dashed border-line-soft hover:border-ink-3/50';
    case 'suggestion':
      return 'bg-status-suggested/10 border-2 border-dashed border-status-suggested/50 hover:border-status-suggested/70 hover:shadow-md';
    default:
      return 'bg-surface-card border border-line-soft hover:border-ink-3/40 hover:shadow-md';
  }
};

export type CourseRole = 'major' | 'minor' | 'general_education' | 'citation';

// Role → color mapping (matches DegreeAudit program type conventions)
export const ROLE_COLORS: Record<CourseRole, string> = {
  major: 'bg-role-major',
  minor: 'bg-role-minor',
  general_education: 'bg-role-gened',
  citation: 'bg-role-citation',
};

const ROLE_ORDER: CourseRole[] = ['major', 'minor', 'general_education', 'citation'];

/**
 * Get ordered role colors for a course based on its audit roles.
 * Returns an array of Tailwind bg classes, e.g. ['bg-blue-500', 'bg-green-500'].
 * Falls back to ['bg-gray-400'] if no roles.
 */
export const getRoleColors = (courseRoles: string[] | null | undefined): string[] => {
  if (!courseRoles || courseRoles.length === 0) {
    return ['bg-gray-400'];
  }
  return ROLE_ORDER.filter((role) => courseRoles.includes(role)).map((role) => ROLE_COLORS[role]);
};

const PILL_COLORS: Record<CourseRole | 'neutral', string> = {
  major: 'bg-blue-100 text-blue-700 border-blue-200',
  minor: 'bg-rose-100 text-rose-700 border-rose-200',
  general_education: 'bg-green-100 text-green-700 border-green-200',
  citation: 'bg-amber-100 text-amber-700 border-amber-200',
  neutral: 'bg-gray-100 text-gray-700 border-gray-200',
};

/**
 * Determine the pill color for a requirement tag based on its course roles.
 * Matches the sidebar indicator bar so colors are always consistent.
 */
export const getRequirementPillColors = (
  course: CourseLikeForType | null | undefined,
  courseRoles: string[] | null | undefined
): string => {
  if (courseRoles && courseRoles.length > 0) {
    for (const role of ROLE_ORDER) {
      if (courseRoles.includes(role)) {
        return PILL_COLORS[role];
      }
    }
  }
  if (course?.requirementKey === 'elective' || course?.requirementKey === 'prerequisite') {
    return PILL_COLORS.neutral;
  }
  if (course?.genEdArea || course?.requirementKey?.startsWith('GEN-')) {
    return PILL_COLORS.general_education;
  }
  return PILL_COLORS.neutral;
};
